import React, { useState } from "react";
import {
  Heart,
  MessageCircle,
  MessageSquare,
  Trash2,
  Edit3,
  MoreHorizontal,
  ExternalLink,
  ArrowRightLeft,
  UserPlus,
  History,
  CheckCircle2,
  Flag,
  EyeOff,
  VolumeX,
  Bell,
  Share2,
  Link2Off,
  UserX,
  UserCheck,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import StarRating from "@/components/ui/StarRating";
import ReviewImageLightbox from "@/components/ReviewImageLightbox";
import Link from "@/components/Link";
import TierChip from "@/components/TierChip";
import { safeExternalUrl } from "@/lib/externalUrl";
import { Review } from "@/types/review";
import { useAuth } from "@/contexts/AuthContext";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { useClampOverflow } from "@/hooks/useClampOverflow";
import { reviewService, type ReviewAuthorHiddenLabel } from "@/api/reviewService";
import { format, formatDistanceToNow } from "date-fns";
import { ar } from "date-fns/locale";

interface ReviewCardProps {
  review: Review;
  // Store the review belongs to — analytics only (the Review type carries no store ref).
  storeId?: string;
  currentUserId?: string;
  isAdmin?: boolean;
  onLike?: (reviewId: string) => void;
  onComment?: (reviewId: string, text: string) => void;
  onEdit?: (review: Review) => void;
  onDelete?: (reviewId: string) => void;
  onReassign?: (review: Review) => void;
  // Admin: link an IMPORTED review to a registered account (or change / undo that link).
  onAssignUser?: (review: Review) => void;
  // Admin: open the review's activity trail (every create / edit / move / link / flag row).
  onViewActivity?: (review: Review) => void;
  // Admin: provenance flags on an IMPORTED review ("original post removed" note, hidden name).
  onSetProvenance?: (
    review: Review,
    patch: { sourceRemoved?: boolean; authorHidden?: ReviewAuthorHiddenLabel | false }
  ) => void;
  onFbComment?: (review: Review) => void;
  onEditRating?: (reviewId: string, rating: number) => void;
  isLoading?: boolean;
  // D-10/UI-SPEC S7: identity for the pinned merchant-reply block (store logo + name).
  storeName?: string;
  storeLogoUrl?: string;
  // Home-feed usage only: renders a linked store identity header (logo + name →
  // /store/slug) above the review. Absent on store-page usage so that layout stays
  // pixel-unchanged.
  feedStore?: { _id: string; name: string; slug?: string; logoUrl?: string };
  // Feed interaction handlers (28-04). Only wired for feed usage; the feed menu is
  // gated on `feedStore` presence. Report never hides the card (server guarantee);
  // hide/mute optimistically remove card(s) from the feed in the parent.
  onReport?: (
    reviewId: string,
    reason: "fake" | "abusive" | "unrelated" | "other",
    note?: string
  ) => void;
  onNotInterestedReview?: (reviewId: string) => void;
  onMuteStore?: (storeId: string) => void;
  onWatchStore?: (storeId: string) => void;
  // Notification deep links (Phase 46, D-09). All optional; absent → card behaves as before.
  // Comments start expanded (a focusComment link must land on a rendered comment).
  defaultShowComments?: boolean;
  // Comment to tint with the primary-soft background and inline-start bar.
  highlightCommentId?: string | null;
  // The pinned card: focusable (tabIndex -1) and carries the highlight transition class.
  isFocused?: boolean;
  // Toggled on by the parent for 2400ms after landing.
  highlighted?: boolean;
}

// Phase 48 D-24: a refund on an order-backed review is disclosed with a neutral tag next to the
// unchanged verified pill. Only Shopify-order verification carries refundedAt; Loox and other
// verified reviews never show it. Native title only (no Radix tooltip) so SSR stays hydration-safe.
const isRefundedPurchase = (review: Review): boolean =>
  review.purchaseVerification?.method === "shopify_order" &&
  Boolean(review.purchaseVerification?.refundedAt);

const RefundedTag = () => (
  <span
    title="تم استرداد قيمة هذا الطلب بعد كتابة المراجعة"
    style={{
      fontFamily: "var(--ys-font)",
      fontSize: 11,
      fontWeight: 500,
      color: "var(--ys-ink-2)",
      background: "var(--ys-bg-2)",
      border: "1px solid var(--ys-rule-2)",
      borderRadius: "var(--ys-r-pill)",
      padding: "1px 7px",
      marginInlineStart: 4,
    }}
  >
    تم الاسترداد
  </span>
);

type SourceInfo = { labelAr: string; dot: string };

// The comment a notification deep link points at: primary-soft tint + 3px inline-start bar.
const highlightedCommentStyle: React.CSSProperties = {
  background: "var(--ys-primary-soft)",
  borderInlineStart: "3px solid var(--ys-primary)",
  borderRadius: "var(--ys-r)",
  padding: 6,
};

function getSourceInfo(externalSource?: string, isComment?: boolean): SourceInfo {
  if (!externalSource) return { labelAr: "يتفرج", dot: "var(--ys-primary)" };
  if (externalSource.includes("google")) return { labelAr: "جوجل", dot: "#ea4335" };
  if (externalSource === "facebook_group") {
    return {
      labelAr: isComment ? "مجموعات فيسبوك (تعليق)" : "مجموعات فيسبوك",
      dot: "#1877f2",
    };
  }
  if (externalSource === "facebook") return { labelAr: "فيسبوك", dot: "#1877f2" };
  if (externalSource === "shopify_widget") {
    return { labelAr: "عميل متجر شوبيفاي", dot: "#96BF48" };
  }
  // Reviews scraped from a merchant's on-site Shopify review app (Loox). Stored under a
  // distinct source so they stay external for counting, but surfaced to readers as what
  // they are from the shopper's point of view: a review left on the store's Shopify shop.
  if (externalSource === "loox") {
    return { labelAr: "متجر شوبيفاي", dot: "#96BF48" };
  }
  if (externalSource === "whatsapp_flow") {
    return { labelAr: "واتساب", dot: "#25D366" };
  }
  return { labelAr: externalSource, dot: "var(--ys-ink-3)" };
}

const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  storeId,
  currentUserId,
  isAdmin = false,
  onLike,
  onComment,
  onEdit,
  onDelete,
  onReassign,
  onAssignUser,
  onViewActivity,
  onSetProvenance,
  onFbComment,
  onEditRating,
  isLoading = false,
  storeName,
  storeLogoUrl,
  feedStore,
  onReport,
  onNotInterestedReview,
  onMuteStore,
  onWatchStore,
  defaultShowComments = false,
  highlightCommentId = null,
  isFocused = false,
  highlighted = false,
}) => {
  // SSR-safe: returns no-op defaults on the server (no AuthProvider mounted).
  const { showAuthModal } = useAuth();
  const { trackReview } = usePostHogTracking();

  const [showComments, setShowComments] = useState(defaultShowComments);
  const [commentText, setCommentText] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const queryClient = useQueryClient();

  const images = review.images ?? [];

  const [editingRating, setEditingRating] = useState(false);
  const [pendingRating, setPendingRating] = useState<number | null>(null);
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // The signed-in reader owns this review when it is theirs natively OR when it is an imported
  // review an admin has linked to their account (`assignedUser`): the API authorizes edit and
  // delete on the same `userId` match either way. A plain scraped review is nobody's to edit.
  const isOwnReview =
    (!review.isExternal || Boolean(review.assignedUser)) && currentUserId === review.user?._id;
  // Feed interactions (like/مفيد + comments) are enabled for ALL reviews incl. external —
  // the backend has no external guard (toggleReviewLike, formatReviewResponse). Only
  // edit/delete stay owner-gated via isOwnReview.
  const canInteract = !!currentUserId;

  // Scraped values — only http/https may ever reach an `href` (T-7r4-01).
  const externalLink = safeExternalUrl(
    review.sourceData?.postUrl ?? review.sourceData?.reviewUrl
  );

  // Long reviews render visually clamped (never sliced, so expand/collapse cannot reflow
  // differently). Whether the clamp actually hides content is DOM-measured post-mount;
  // the clamp style itself applies whenever collapsed (harmless for short text). The
  // toggle supplies the bottom gap when present, so the text element drops to 4px and
  // the rhythm before the images/actions row stays unchanged.
  const { ref: textRef, isTruncated } = useClampOverflow<HTMLElement>(
    expanded,
    review.text
  );
  const isClamped = !expanded;
  const reviewTextStyle: React.CSSProperties = {
    fontFamily: "var(--ys-font)",
    fontSize: 14,
    color: "var(--ys-ink-2)",
    lineHeight: 1.65,
    marginTop: 0,
    marginBottom: isTruncated ? 4 : 12,
    ...(isClamped
      ? {
          display: "-webkit-box",
          WebkitLineClamp: 6,
          WebkitBoxOrient: "vertical" as const,
          overflow: "hidden",
        }
      : {}),
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: ar });
    } catch {
      return "";
    }
  };

  const displayDate = review.isExternal && review.externalDate
    ? formatDate(review.externalDate)
    : formatDate(review.createdAt);

  // Admin-confirmed note that the imported review's original post is gone at its source. Neutral
  // on purpose: it states what we know (where and when it was posted, that the post is no longer
  // available) and never why it was removed.
  const sourceRemovedNote = (() => {
    if (!review.isExternal || !review.sourceRemovedAt) return null;
    const where =
      review.externalSource === "facebook_group"
        ? "في جروب فيسبوك"
        : `على ${getSourceInfo(review.externalSource).labelAr}`;
    let when = "";
    try {
      when = review.externalDate
        ? ` بتاريخ ${format(new Date(review.externalDate), "d MMMM yyyy", { locale: ar })}`
        : "";
    } catch {
      when = "";
    }
    return `نُشرت ${where}${when}، والمنشور الأصلي لم يعد متاحًا.`;
  })();

  // An imported review an admin has linked to a registered account. It renders as that account
  // (name, tier, profile link) while `isExternal` keeps the source badge and the outbound link.
  const isLinkedExternal = Boolean(review.isExternal && review.assignedUser);

  const displayName = review.isAnonymous
    ? "مستخدم مجهول"
    : review.isExternal && !isLinkedExternal && review.externalAuthor
    ? review.externalAuthor
    : review.user?.full_name || "مستخدم";

  const avatarInitial = displayName?.[0]?.toUpperCase() || "م";

  // The author's tier chip and the link through to their profile, or null.
  //
  // Both are suppressed on ANONYMOUS and IMPORTED reviews (spec §8, §9): an anonymous review has
  // deliberately detached its author, and an imported one has no account behind the scraped name,
  // so a tier title there would claim a contribution history that does not exist. The server
  // already omits `tier` for external reviews; this is the second lock, not the only one.
  // A LINKED imported review is the exception on both sides: an account stands behind it and
  // was paid for it, so it gets the chip and the profile link like a native review.
  const hasAccountAuthor = !review.isAnonymous && (!review.isExternal || isLinkedExternal);
  const authorTier = hasAccountAuthor && review.user?.tier ? review.user.tier : null;
  const authorProfileHref =
    hasAccountAuthor && review.user?._id ? `/u/${review.user._id}` : null;

  const handleTierChipClick = () => {
    if (!authorTier || !review.user?._id) return;
    trackReview.tierChipClicked({
      surface: "review_card",
      tier_level: authorTier.level,
      target_user_id: review.user._id,
    });
  };
  const src = getSourceInfo(
    review.isExternal ? review.externalSource : undefined,
    review.sourceData?.isComment as boolean | undefined
  );

  // «تقييمك» — shown on the reader's own card in both header variants.
  const ownChip = isOwnReview ? (
    <span
      style={{
        fontFamily: "var(--ys-font)",
        fontSize: 11,
        fontWeight: 600,
        color: "var(--ys-primary)",
        background: "var(--ys-primary-soft)",
        border: "1px solid var(--ys-primary)",
        borderRadius: "var(--ys-r-pill)",
        padding: "1px 7px",
      }}
    >
      تقييمك
    </span>
  ) : null;
  const currentRating = pendingRating ?? review.overallRating;

  // Fake-campaign disclosure. The review is NOT hidden — it just does not count toward the
  // store's rating, and the reader is told exactly why. Rendered next to the source chip in
  // both the feed and store-page header variants.
  const campaignChip = review.inauthentic?.flagged ? (
    <span
      title="تم رصد عدد كبير من التقييمات خلال فترة قصيرة — لا تُحتسب ضمن تقييم المتجر"
      style={{
        fontFamily: "var(--ys-font)",
        fontSize: 11,
        fontWeight: 500,
        color: "#92400e",
        background: "#fef3c7",
        border: "1px solid #f59e0b",
        borderRadius: "var(--ys-r-pill)",
        padding: "1px 7px",
      }}
    >
      ⚠️ ضمن حملة تقييمات على صفحة المتجر
    </span>
  ) : null;

  // D-10/UI-SPEC S7: the merchant reply renders above the actions row and is always
  // visible, visually distinct from user comments. At most one (one-per-review, D-10).
  const merchantReply = review.comments.find((c) => c.isMerchantReply);
  const userComments = review.comments.filter((c) => !c.isMerchantReply);
  // A store reply speaks for the BUSINESS, never for a person. The old fallback read the
  // reply comment's author full_name, which publishes a real name under the "رد المتجر" badge
  // (and the acting admin's own name when an admin replies on the store's behalf). feedStore
  // is already passed by the home feed; the merchant portal passes storeName explicitly.
  const replyStoreName = storeName || feedStore?.name || "المتجر";
  const replyStoreInitial = replyStoreName?.[0]?.toUpperCase() || "م";

  // «مشاركة»: a Shlink short link to /review/<id>, which redirects to the store page with this
  // review pinned (?focusReview). The link is minted once per review and cached server-side; the
  // query cache below keeps a second tap from making another request.
  const shareQueryKey = ["reviewShareLink", review._id];
  const fetchShareUrl = () =>
    queryClient.fetchQuery({
      queryKey: shareQueryKey,
      queryFn: () => reviewService.getShareLink(review._id),
      staleTime: Infinity,
    });
  // Warm the link on press so the share sheet usually opens without waiting on the network.
  const prefetchShareUrl = () => {
    void queryClient.prefetchQuery({
      queryKey: shareQueryKey,
      queryFn: () => reviewService.getShareLink(review._id),
      staleTime: Infinity,
    });
  };
  const trackShared = (method: "native" | "clipboard") =>
    trackReview.shared({
      review_id: review._id,
      store_id: storeId ?? feedStore?._id ?? "",
      method,
      surface: feedStore ? "feed" : "store_page",
    });
  const copyShareUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      trackShared("clipboard");
      toast.success("تم نسخ رابط التقييم");
    } catch {
      // Safari drops the tap's user activation across the network await, so the copy is retried
      // from a fresh tap on the toast's own button.
      toast("رابط التقييم جاهز", {
        description: url,
        action: { label: "نسخ", onClick: () => void copyShareUrl(url) },
      });
    }
  };
  const handleShare = async () => {
    if (typeof window === "undefined" || isSharing) return;
    setIsSharing(true);
    let url: string;
    try {
      url = await fetchShareUrl();
    } catch {
      toast.error("تعذر إنشاء رابط المشاركة");
      return;
    } finally {
      setIsSharing(false);
    }
    const shareStoreName = storeName ?? feedStore?.name;
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: shareStoreName ? `تقييم ${shareStoreName}` : "تقييم على يتفرج",
          text: shareStoreName ? `شوف التقييم ده عن ${shareStoreName} على يتفرج` : "شوف التقييم ده على يتفرج",
          url,
        });
        trackShared("native");
        return;
      } catch (error) {
        // The reader closed the share sheet: not a failure, and no copy fallback.
        if ((error as { name?: string })?.name === "AbortError") return;
        // NotAllowedError (activation lost) or no share target: fall through to copying.
      }
    }
    await copyShareUrl(url);
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim() || !onComment) return;
    setIsSubmittingComment(true);
    try {
      await onComment(review._id, commentText.trim());
      trackReview.commentAdded(review._id, storeId ?? feedStore?._id ?? "");
      setCommentText("");
    } catch {
      // handled by parent
    } finally {
      setIsSubmittingComment(false);
    }
  };

  return (
    <>
      <div
        id={`review-${review._id}`}
        tabIndex={isFocused ? -1 : undefined}
        className={
          isFocused ? `ys-focus-target${highlighted ? " ys-focus-highlight" : ""}` : undefined
        }
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          scrollMarginTop: 72,
          // The reader's own review is pinned first by the server and tinted here so it reads as
          // "yours" at a glance rather than as one more card in the list.
          background: isOwnReview ? "var(--ys-primary-tint, var(--ys-paper))" : "var(--ys-paper)",
          borderRadius: "var(--ys-r-md)",
          border: `1px solid ${
            isOwnReview ? "var(--ys-primary)" : hovered ? "var(--ys-rule-2)" : "var(--ys-rule)"
          }`,
          boxShadow: hovered ? "var(--ys-shadow-3)" : "var(--ys-shadow-1)",
          padding: "18px 20px",
          transform: hovered ? "translateY(-1px)" : "translateY(0)",
          // The pinned card leaves transitions to `.ys-focus-target` so the 600ms highlight fade
          // and its reduced-motion override are not shadowed by this inline value.
          transition: isFocused
            ? undefined
            : "box-shadow 150ms var(--ys-ease), border-color 150ms var(--ys-ease), transform 150ms var(--ys-ease)",
          direction: "rtl",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 12,
          }}
        >
          {/* Left: identity + meta. Feed usage leads with the REVIEWED STORE
              (logo + linked name) so the store is the card's hero; the review
              author drops to the meta line. Store-page usage (no feedStore)
              keeps the original author-led header pixel-unchanged. */}
          {feedStore ? (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0 }}>
              <Link
                to={`/store/${feedStore.slug || feedStore._id}`}
                aria-hidden="true"
                tabIndex={-1}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "var(--ys-r-md)",
                  background: "var(--ys-bg-2)",
                  color: "var(--ys-ink-2)",
                  fontFamily: "var(--ys-font)",
                  fontWeight: 600,
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  overflow: "hidden",
                  textDecoration: "none",
                }}
              >
                {feedStore.logoUrl ? (
                  <img
                    src={feedStore.logoUrl}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  feedStore.name?.[0]?.toUpperCase() || "م"
                )}
              </Link>

              <div style={{ minWidth: 0 }}>
                {/* Store name row */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <Link
                    to={`/store/${feedStore.slug || feedStore._id}`}
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontWeight: 700,
                      fontSize: 15,
                      color: "var(--ys-ink)",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                    }}
                  >
                    {feedStore.name}
                  </Link>
                  {review.isVerifiedPurchase && (
                    <span
                      style={{
                        fontFamily: "var(--ys-font)",
                        fontSize: 11,
                        fontWeight: 500,
                        color: "var(--ys-success)",
                        background: "var(--ys-success-soft)",
                        border: "1px solid var(--ys-success)",
                        borderRadius: "var(--ys-r-pill)",
                        padding: "1px 7px",
                      }}
                    >
                      عميل موثق
                    </span>
                  )}
                  {isRefundedPurchase(review) && <RefundedTag />}
                </div>

                {/* Author + source + timestamp row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 3,
                    flexWrap: "wrap",
                    fontFamily: "var(--ys-font)",
                    fontSize: 12,
                    color: "var(--ys-ink-3)",
                  }}
                >
                  <span>
                    تقييم من{" "}
                    {authorProfileHref ? (
                      <Link
                        to={authorProfileHref}
                        style={{ color: "inherit", textDecoration: "underline" }}
                      >
                        {displayName}
                      </Link>
                    ) : (
                      displayName
                    )}
                  </span>
                  {authorTier && (
                    <TierChip
                      tier={authorTier}
                      href={authorProfileHref ?? undefined}
                      onClick={handleTierChipClick}
                    />
                  )}
                  {ownChip}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: src.dot,
                        flexShrink: 0,
                        display: "inline-block",
                      }}
                    />
                    {src.labelAr}
                  </span>
                  {campaignChip}
                  {displayDate && <span>· {displayDate}</span>}
                </div>
              </div>
            </div>
          ) : (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0 }}>
            {/* Initial circle */}
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "var(--ys-r-pill)",
                background: "var(--ys-bg-2)",
                color: "var(--ys-ink-2)",
                fontFamily: "var(--ys-font)",
                fontWeight: 600,
                fontSize: 15,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {avatarInitial}
            </div>

            <div style={{ minWidth: 0 }}>
              {/* Name row */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {authorProfileHref ? (
                  <Link
                    to={authorProfileHref}
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontWeight: 600,
                      fontSize: 14,
                      color: "var(--ys-ink)",
                      textDecoration: "none",
                    }}
                  >
                    {displayName}
                  </Link>
                ) : (
                  <span
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontWeight: 600,
                      fontSize: 14,
                      color: "var(--ys-ink)",
                    }}
                  >
                    {displayName}
                  </span>
                )}
                {authorTier && (
                  <TierChip
                    tier={authorTier}
                    href={authorProfileHref ?? undefined}
                    onClick={handleTierChipClick}
                  />
                )}
                {review.isVerifiedPurchase && (
                  <span
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontSize: 11,
                      fontWeight: 500,
                      color: "var(--ys-success)",
                      background: "var(--ys-success-soft)",
                      border: "1px solid var(--ys-success)",
                      borderRadius: "var(--ys-r-pill)",
                      padding: "1px 7px",
                    }}
                  >
                    عميل موثق
                  </span>
                )}
                {isRefundedPurchase(review) && <RefundedTag />}
                {ownChip}
              </div>

              {/* Source + timestamp row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 3,
                  flexWrap: "wrap",
                }}
              >
                {/* Source dot + label */}
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontFamily: "var(--ys-font)",
                    fontSize: 12,
                    color: "var(--ys-ink-3)",
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: src.dot,
                      flexShrink: 0,
                      display: "inline-block",
                    }}
                  />
                  {src.labelAr}
                </span>
                {/* Admin-only: which scraped name this linked review was imported under. */}
                {isAdmin && isLinkedExternal && review.externalAuthor && (
                  <span
                    title="اسم الكاتب في المصدر الأصلي"
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontSize: 11,
                      color: "var(--ys-ink-3)",
                      background: "var(--ys-surface-2, #f3f4f6)",
                      borderRadius: "var(--ys-r-pill)",
                      padding: "1px 7px",
                    }}
                  >
                    باسم {review.externalAuthor}
                  </span>
                )}
                {campaignChip}
                {displayDate && (
                  <span
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontSize: 12,
                      color: "var(--ys-ink-3)",
                    }}
                  >
                    · {displayDate}
                  </span>
                )}
                {/* Author-edit disclosure — never shown for admin corrections (see Review.editedAt). */}
                {review.editedAt && (
                  <span
                    title={`آخر تعديل ${formatDate(review.editedAt)}`}
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontSize: 12,
                      color: "var(--ys-ink-3)",
                    }}
                  >
                    · تم التعديل
                  </span>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Right: actions */}
          {/* marginInlineEnd pulls the 28×28 icon buttons toward the card content edge
              so the centered icon optically aligns with it (buttons carry ~6px internal
              padding), keeping all header controls consistent and not cramped. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              flexShrink: 0,
              marginInlineEnd: -6,
            }}
          >
            {externalLink && (
              <a
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: "var(--ys-r-sm)",
                  color: "var(--ys-ink-3)",
                  textDecoration: "none",
                }}
              >
                <ExternalLink style={{ width: 14, height: 14 }} />
              </a>
            )}

            {isAdmin && onFbComment && review.externalSource === "facebook_group" && review.sourceData?.postUrl && (
              review.commented ? (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontFamily: "var(--ys-font)",
                    fontSize: 11,
                    color: "var(--ys-success)",
                    padding: "2px 6px",
                  }}
                >
                  <CheckCircle2 style={{ width: 12, height: 12 }} />
                  علق
                </span>
              ) : (
                <button
                  onClick={() => onFbComment(review)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontFamily: "var(--ys-font)",
                    fontSize: 11,
                    color: "#1877f2",
                    background: "#e7f0fd",
                    border: "none",
                    borderRadius: "var(--ys-r-sm)",
                    padding: "3px 8px",
                    cursor: "pointer",
                  }}
                >
                  <MessageSquare style={{ width: 11, height: 11 }} />
                  علق
                </button>
              )
            )}

            {(isOwnReview || isAdmin) &&
              (onEdit || onDelete || onReassign || onAssignUser || onViewActivity || onSetProvenance) && (
              <DropdownMenu dir="rtl">
                <DropdownMenuTrigger asChild>
                  <button
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 28,
                      height: 28,
                      borderRadius: "var(--ys-r-sm)",
                      background: "none",
                      border: "none",
                      color: "var(--ys-ink-3)",
                      cursor: "pointer",
                    }}
                  >
                    <MoreHorizontal style={{ width: 16, height: 16 }} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onEdit && isOwnReview && (
                    <DropdownMenuItem onClick={() => onEdit(review)}>
                      <Edit3 className="w-4 h-4 me-2" />
                      تعديل
                    </DropdownMenuItem>
                  )}
                  {onReassign && isAdmin && review.externalSource === "facebook_group" && (
                    <DropdownMenuItem onClick={() => onReassign(review)}>
                      <ArrowRightLeft className="w-4 h-4 me-2" />
                      نقل لمتجر آخر
                    </DropdownMenuItem>
                  )}
                  {onAssignUser && isAdmin && review.isExternal && (
                    <DropdownMenuItem onClick={() => onAssignUser(review)}>
                      <UserPlus className="w-4 h-4 me-2" />
                      {isLinkedExternal ? "تغيير المستخدم المرتبط" : "ربط بمستخدم مسجّل"}
                    </DropdownMenuItem>
                  )}
                  {onSetProvenance && isAdmin && review.isExternal && (
                    <>
                      <DropdownMenuItem
                        onClick={() =>
                          onSetProvenance(review, { sourceRemoved: !review.sourceRemovedAt })
                        }
                      >
                        <Link2Off className="w-4 h-4 me-2" />
                        {review.sourceRemovedAt
                          ? "إلغاء ملاحظة حذف المنشور الأصلي"
                          : "المنشور الأصلي اتمسح"}
                      </DropdownMenuItem>
                      {review.authorHidden ? (
                        <DropdownMenuItem
                          onClick={() => onSetProvenance(review, { authorHidden: false })}
                        >
                          <UserCheck className="w-4 h-4 me-2" />
                          إظهار اسم صاحب التقييم
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <UserX className="w-4 h-4 me-2" />
                            إخفاء اسم صاحب التقييم
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            <DropdownMenuItem
                              onClick={() => onSetProvenance(review, { authorHidden: "female" })}
                            >
                              يظهر «مشترية»
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onSetProvenance(review, { authorHidden: "male" })}
                            >
                              يظهر «مشتري»
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      )}
                    </>
                  )}
                  {onViewActivity && isAdmin && (
                    <DropdownMenuItem onClick={() => onViewActivity(review)}>
                      <History className="w-4 h-4 me-2" />
                      سجل التغييرات
                    </DropdownMenuItem>
                  )}
                  {onDelete && (
                    <DropdownMenuItem
                      onClick={() => onDelete(review._id)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4 me-2" />
                      حذف
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* ── Feed interaction menu (feed usage only) ── */}
            {feedStore &&
              (onReport || onNotInterestedReview || onMuteStore || onWatchStore) && (
                <DropdownMenu dir="rtl">
                  <DropdownMenuTrigger asChild>
                    <button
                      aria-label="خيارات التقييم"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: "var(--ys-r-sm)",
                        background: "none",
                        border: "none",
                        color: "var(--ys-ink-3)",
                        cursor: "pointer",
                      }}
                    >
                      <MoreHorizontal style={{ width: 16, height: 16 }} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onWatchStore && (
                      <DropdownMenuItem onClick={() => onWatchStore(feedStore._id)}>
                        <Bell className="w-4 h-4 me-2" />
                        تابع المتجر
                      </DropdownMenuItem>
                    )}
                    {onNotInterestedReview && (
                      <DropdownMenuItem
                        onClick={() => onNotInterestedReview(review._id)}
                      >
                        <EyeOff className="w-4 h-4 me-2" />
                        مش مهتم بالتقييم ده
                      </DropdownMenuItem>
                    )}
                    {onMuteStore && (
                      <DropdownMenuItem onClick={() => onMuteStore(feedStore._id)}>
                        <VolumeX className="w-4 h-4 me-2" />
                        كتّم متجر {feedStore.name}
                      </DropdownMenuItem>
                    )}
                    {onReport && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <Flag className="w-4 h-4 me-2" />
                            الإبلاغ عن هذا التقييم
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            <DropdownMenuItem
                              onClick={() => onReport(review._id, "fake")}
                            >
                              تقييم مزيّف
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onReport(review._id, "abusive")}
                            >
                              مسيء أو مخالف
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onReport(review._id, "unrelated")}
                            >
                              غير متعلّق بالمتجر
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onReport(review._id, "other")}
                            >
                              سبب آخر
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
          </div>
        </div>

        {/* ── Star rating ── */}
        <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
          {editingRating ? (
            <>
              <StarRating
                rating={currentRating}
                onRatingChange={(r) => {
                  setPendingRating(r);
                  setEditingRating(false);
                  onEditRating?.(review._id, r);
                }}
                showNumber
              />
              <button
                onClick={() => setEditingRating(false)}
                style={{
                  fontFamily: "var(--ys-font)",
                  fontSize: 12,
                  color: "var(--ys-ink-3)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                إلغاء
              </button>
            </>
          ) : (
            <>
              {/* Inline star chars for read-only display */}
              <span style={{ direction: "ltr", display: "inline-flex", alignItems: "center", gap: 1 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: 15,
                      color: "var(--ys-star)",
                      opacity: i <= Math.round(currentRating) ? 1 : 0.2,
                    }}
                  >
                    ★
                  </span>
                ))}
              </span>
              <span
                style={{
                  fontFamily: "var(--ys-font)",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--ys-ink-2)",
                }}
              >
                {currentRating.toFixed(1)}
              </span>
              {isAdmin && onEditRating && (
                <button
                  onClick={() => setEditingRating(true)}
                  title="تعديل التقييم"
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--ys-rule-2)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    padding: 0,
                  }}
                >
                  <Edit3 style={{ width: 13, height: 13 }} />
                </button>
              )}
            </>
          )}
        </div>

        {/* ── Review text ── */}
        {review.text && (
          <>
            {externalLink ? (
              <a
                ref={textRef as React.RefObject<HTMLAnchorElement>}
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  ...reviewTextStyle,
                  textDecoration: "none",
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = "var(--ys-primary)")}
                onMouseOut={(e) => (e.currentTarget.style.color = "var(--ys-ink-2)")}
              >
                {review.text}
              </a>
            ) : (
              <p
                ref={textRef as React.RefObject<HTMLParagraphElement>}
                style={reviewTextStyle}
              >
                {review.text}
              </p>
            )}
            {/* Rendered OUTSIDE the external-link anchor so expanding never navigates away. */}
            {isTruncated && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                style={{
                  fontFamily: "var(--ys-font)",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--ys-primary)",
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  marginBottom: 12,
                }}
              >
                {expanded ? "عرض أقل" : "عرض المزيد"}
              </button>
            )}
          </>
        )}
        {sourceRemovedNote && (
          <p
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 12,
              color: "var(--ys-ink-3)",
              lineHeight: 1.6,
              marginTop: 0,
              marginBottom: 12,
            }}
          >
            {sourceRemovedNote}
          </p>
        )}

        {/* ── Images ── */}
        {/* Feed usage: Facebook-style layout — a single image renders as a
            full-width hero, 2 side-by-side, 3 as hero + pair, 4+ as a 2×2 grid
            with a "+N" overlay on the last tile. Store-page usage keeps the
            original 72px thumbnails. Both open the shared lightbox by index. */}
        {images.length > 0 && feedStore ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: images.length === 1 ? "1fr" : "1fr 1fr",
              gap: 6,
              marginBottom: 12,
            }}
          >
            {images.slice(0, 4).map((img, idx) => {
              const isHero = images.length === 1 || (images.length === 3 && idx === 0);
              const tileHeight =
                images.length === 1
                  ? undefined
                  : isHero
                    ? 280
                    : images.length === 2
                      ? 240
                      : 200;
              const extraCount =
                images.length > 4 && idx === 3 ? images.length - 4 : 0;
              return (
                <button
                  key={idx}
                  onClick={() => setLightboxIdx(idx)}
                  aria-label={`صورة ${idx + 1} من ${images.length}`}
                  style={{
                    position: "relative",
                    gridColumn: isHero && images.length === 3 ? "span 2" : undefined,
                    height: tileHeight,
                    borderRadius: "var(--ys-r)",
                    overflow: "hidden",
                    border: "1px solid var(--ys-rule)",
                    background: "var(--ys-bg-2)",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <img
                    src={img}
                    alt=""
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: images.length === 1 ? "auto" : "100%",
                      maxHeight: images.length === 1 ? 420 : undefined,
                      objectFit: "cover",
                      display: "block",
                    }}
                    onError={(e) => {
                      // Expired/broken source URLs (e.g. old FB CDN links) — hide the tile
                      const btn = e.currentTarget.parentElement;
                      if (btn) btn.style.display = "none";
                    }}
                  />
                  {extraCount > 0 && (
                    <span
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(0,0,0,.45)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: "var(--ys-font)",
                        fontWeight: 700,
                        fontSize: 22,
                      }}
                    >
                      +{extraCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ) : images.length > 0 ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setLightboxIdx(idx)}
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "var(--ys-r)",
                  overflow: "hidden",
                  border: "1px solid var(--ys-rule)",
                  cursor: "pointer",
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                <img
                  src={img}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    // Expired/broken source URLs (e.g. old FB CDN links) — hide the tile
                    const btn = e.currentTarget.parentElement;
                    if (btn) btn.style.display = "none";
                  }}
                />
              </button>
            ))}
          </div>
        ) : null}

        {/* D-10/UI-SPEC S7: merchant reply is always visible, above the actions row. */}
        {merchantReply && (
          <div
            key={merchantReply._id}
            id={`comment-${merchantReply._id}`}
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              marginTop: 12,
              scrollMarginTop: 72,
              ...(highlightCommentId === merchantReply._id ? highlightedCommentStyle : {}),
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "var(--ys-r-pill)",
                background: "var(--ys-bg-2)",
                color: "var(--ys-ink-2)",
                fontFamily: "var(--ys-font)",
                fontWeight: 600,
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {storeLogoUrl ? (
                <img
                  src={storeLogoUrl}
                  alt={replyStoreName}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                replyStoreInitial
              )}
            </div>
            <div
              style={{
                flex: 1,
                background: "var(--ys-primary-soft)",
                borderRadius: "var(--ys-r)",
                borderInlineStart: "3px solid var(--ys-primary)",
                padding: "8px 12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--ys-font)",
                    fontWeight: 600,
                    fontSize: 14,
                    color: "var(--ys-ink)",
                  }}
                >
                  {replyStoreName}
                </span>
                <span
                  style={{
                    fontFamily: "var(--ys-font)",
                    fontWeight: 600,
                    fontSize: 11,
                    color: "var(--ys-primary)",
                    background: "var(--ys-paper)",
                    border: "1px solid var(--ys-primary)",
                    borderRadius: "var(--ys-r-pill)",
                    padding: "1px 8px",
                  }}
                >
                  رد المتجر
                </span>
                <span
                  style={{
                    fontFamily: "var(--ys-font)",
                    fontSize: 11,
                    color: "var(--ys-ink-3)",
                  }}
                >
                  {formatDate(merchantReply.createdAt)}
                </span>
              </div>
              <p
                style={{
                  fontFamily: "var(--ys-font)",
                  fontSize: 14,
                  color: "var(--ys-ink-2)",
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                {merchantReply.text}
              </p>
            </div>
          </div>
        )}

        {/* ── Like / comment actions (enabled for all reviews incl. external) ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              paddingTop: 12,
              borderTop: "1px solid var(--ys-rule)",
            }}
          >
            <button
              onClick={() => {
                if (!canInteract) {
                  showAuthModal({ trigger: "contribute" });
                  return;
                }
                onLike?.(review._id);
              }}
              disabled={isLoading}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontFamily: "var(--ys-font)",
                fontSize: 13,
                color: review.isLikedByCurrentUser ? "var(--ys-primary)" : "var(--ys-ink-3)",
                background: "none",
                border: "none",
                borderRadius: "var(--ys-r)",
                padding: "5px 10px",
                cursor: "pointer",
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              <Heart
                style={{
                  width: 15,
                  height: 15,
                  fill: review.isLikedByCurrentUser ? "currentColor" : "none",
                }}
              />
              {review.likesCount > 0 ? `${review.likesCount} مفيد` : "مفيد"}
            </button>

            <button
              onClick={() => setShowComments(!showComments)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontFamily: "var(--ys-font)",
                fontSize: 13,
                color: "var(--ys-ink-3)",
                background: "none",
                border: "none",
                borderRadius: "var(--ys-r)",
                padding: "5px 10px",
                cursor: "pointer",
              }}
            >
              <MessageCircle style={{ width: 15, height: 15 }} />
              {review.commentsCount > 0 ? `${review.commentsCount} تعليق` : "تعليق"}
            </button>

            <button
              onClick={handleShare}
              onPointerDown={prefetchShareUrl}
              disabled={isSharing}
              aria-label="مشاركة التقييم"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontFamily: "var(--ys-font)",
                fontSize: 13,
                color: "var(--ys-ink-3)",
                background: "none",
                border: "none",
                borderRadius: "var(--ys-r)",
                padding: "5px 10px",
                cursor: isSharing ? "default" : "pointer",
                opacity: isSharing ? 0.6 : 1,
              }}
            >
              <Share2 style={{ width: 15, height: 15 }} />
              مشاركة
            </button>

            {/* External reviews only: the header icon and the text-as-link are easy to miss on
                mobile, so the source gets a labelled action of its own. */}
            {review.isExternal && externalLink && (
              <a
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackReview.sourceClicked({
                    review_id: review._id,
                    store_id: storeId ?? feedStore?._id ?? "",
                    external_source: review.externalSource ?? "unknown",
                    surface: "review_card_actions",
                  })
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontFamily: "var(--ys-font)",
                  fontSize: 13,
                  color: "var(--ys-ink-3)",
                  background: "none",
                  border: "none",
                  borderRadius: "var(--ys-r)",
                  padding: "5px 10px",
                  cursor: "pointer",
                  textDecoration: "none",
                  marginInlineStart: "auto",
                }}
              >
                <ExternalLink style={{ width: 15, height: 15 }} />
                عرض المصدر
              </a>
            )}
          </div>

        {/* ── Comments section ── */}
        {showComments && (
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: "1px solid var(--ys-rule)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {canInteract ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Textarea
                  placeholder="اكتب تعليقك..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className="font-arabic"
                  rows={2}
                />
                <div>
                  <button
                    onClick={handleSubmitComment}
                    disabled={!commentText.trim() || isSubmittingComment}
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontSize: 13,
                      fontWeight: 600,
                      background: "var(--ys-primary)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "var(--ys-r)",
                      padding: "7px 16px",
                      cursor: !commentText.trim() || isSubmittingComment ? "default" : "pointer",
                      opacity: !commentText.trim() || isSubmittingComment ? 0.6 : 1,
                    }}
                  >
                    {isSubmittingComment ? "جاري الإرسال..." : "إرسال"}
                  </button>
                </div>
              </div>
            ) : (
              // Guests can read the comments below; the composer is replaced by a login prompt.
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div>
                  <button
                    type="button"
                    onClick={() => showAuthModal({ trigger: "comment" })}
                    style={{
                      fontFamily: "var(--ys-font)",
                      fontSize: 13,
                      fontWeight: 600,
                      background: "var(--ys-primary)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "var(--ys-r)",
                      padding: "7px 16px",
                      cursor: "pointer",
                    }}
                  >
                    سجّل دخولك للتعليق
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {userComments.map((comment) => (
                <div
                  key={comment._id}
                  id={`comment-${comment._id}`}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    scrollMarginTop: 72,
                    ...(highlightCommentId === comment._id ? highlightedCommentStyle : {}),
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "var(--ys-r-pill)",
                      background: "var(--ys-bg-2)",
                      color: "var(--ys-ink-2)",
                      fontFamily: "var(--ys-font)",
                      fontWeight: 600,
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {comment.user.full_name?.[0]?.toUpperCase() || "م"}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      background: "var(--ys-bg)",
                      borderRadius: "var(--ys-r)",
                      padding: "8px 12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "var(--ys-font)",
                          fontWeight: 600,
                          fontSize: 13,
                          color: "var(--ys-ink)",
                        }}
                      >
                        {comment.user.full_name || "مستخدم"}
                      </span>
                      <span
                        style={{
                          fontFamily: "var(--ys-font)",
                          fontSize: 11,
                          color: "var(--ys-ink-3)",
                        }}
                      >
                        {formatDate(comment.createdAt)}
                      </span>
                    </div>
                    <p
                      style={{
                        fontFamily: "var(--ys-font)",
                        fontSize: 13,
                        color: "var(--ys-ink-2)",
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      {comment.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Lightbox ── */}
      {/* Shared, portal-rendered so it is never clipped by a transformed ancestor.
          Index-based with wrap-around; Escape closes, arrows navigate. */}
      <ReviewImageLightbox
        images={images}
        index={lightboxIdx}
        onChange={setLightboxIdx}
        onClose={() => setLightboxIdx(null)}
      />
    </>
  );
};

export default ReviewCard;
